import { isString, isFinite, isEqual } from 'lodash-es';
import Dinero from 'dinero.js';

const THOUSAND = 1000;
const MILLION = 1000000;

export function formatCurrency(rawAmount, currency, showMinor = true) {
  let amount = parseFloat(rawAmount);

  if (!isFinite(amount) || !isString(currency)) {
    return '';
  }

  amount = Math.round(amount * 100);

  const format = showMinor ? undefined : '$0,0';

  return Dinero({ amount, currency }).toFormat(format);
}

export function formatCurrencyBigNumbers(rawAmount, currency) {
  let marker = '';
  let amount = Math.abs(rawAmount);

  if (amount >= THOUSAND && amount < MILLION) {
    marker = 'K';
    amount /= THOUSAND;
  } else if (amount >= MILLION) {
    marker = 'M';
    amount /= MILLION;
  }

  amount = rawAmount < 0 ? amount * -1 : amount;

  return `${formatCurrency(amount, currency, false)}${marker}`;
}

export function getDineroMask(rawAmount, scale, roundingBreakpoint = 0.005) {
  const baseMask = '$0,0';

  // No scale (regular currency) → default mask (no decimals)
  if (!scale || !isFinite(scale.value) || scale.value === 0) {
    return baseMask;
  }

  const magnitude = Math.abs(rawAmount);
  const scaledValue = magnitude / scale.value;
  const integerPart = Math.floor(scaledValue);
  const fractionalPart = scaledValue - integerPart;

  // If scaled value is very close to an integer, treat as integer (no decimals)
  if (fractionalPart < roundingBreakpoint || fractionalPart > 1 - roundingBreakpoint) {
    return baseMask; // e.g. 5M
  }

  // Now choose decimals by scale:
  // - K → 1 decimal
  if (scale.value === 1e3) {
    return `${baseMask}.0`; // "$0,0.0"
  }

  // for 1e6 (M) and 1e9 (B)
  return `${baseMask}.00`; // "$0,0.00"
}

export function formatCurrencyScaledBigNumbers(rawAmount, currency, roundingBreakpoint = 0.005) {
  const scales = [
    { value: 1e9, suffix: 'B' },
    { value: 1e6, suffix: 'M' },
    { value: 1e3, suffix: 'K' },
    { value: 0, suffix: '' },
  ];

  const magnitude = Math.abs(rawAmount);
  const scale = scales.find((s) => s.value <= magnitude) || { value: 0, suffix: '' };

  const mask = getDineroMask(magnitude, scale, roundingBreakpoint);

  let scaledMinorAmount;
  if (scale.value === 0) {
    // no scaling – just regular currency in minor units
    scaledMinorAmount = Math.round(rawAmount * 100);
  } else {
    // scale first, then convert to minor units and round
    scaledMinorAmount = Math.round((rawAmount * 100) / scale.value);
  }

  const result = Dinero({ amount: scaledMinorAmount, currency }).toFormat(mask);

  return result + scale.suffix;
}
